import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FileUpload from "@/components/FileUpload";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Building2, ArrowRight, ShieldCheck, BadgeCheck, X, Hourglass } from "lucide-react";
import { toast } from "sonner";

const VENUE_TYPES = ["hotel", "resort", "villa", "airbnb", "guesthouse", "other"];
const CITIES = ["Kingston", "Portmore", "Spanish Town", "Montego Bay", "Ocho Rios", "Negril", "Mandeville", "Port Antonio"];

export default function VenueRegisterPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [venue, setVenue] = useState(null);
    const [loading, setLoading] = useState(true);

    const [name, setName] = useState("");
    const [venueType, setVenueType] = useState("hotel");
    const [contactName, setContactName] = useState("");
    const [contactEmail, setContactEmail] = useState("");
    const [contactPhone, setContactPhone] = useState("");
    const [address, setAddress] = useState("");
    const [city, setCity] = useState("Montego Bay");
    const [rooms, setRooms] = useState("");
    const [notes, setNotes] = useState("");
    const [logoFileId, setLogoFileId] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!user) {
            setLoading(false);
            return;
        }
        api.get("/venues/me")
            .then(({ data }) => {
                setVenue(data);
                setLoading(false);
            })
            .catch(() => {
                if (user.email) setContactEmail(user.email);
                if (user.name) setContactName(user.name);
                setLoading(false);
            });
    }, [user]);

    const submit = async (e) => {
        e.preventDefault();
        if (!user) {
            navigate("/auth?mode=register");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post("/venues/register", {
                name,
                venue_type: venueType,
                contact_name: contactName,
                contact_email: contactEmail,
                contact_phone: contactPhone || null,
                address,
                city,
                rooms: rooms ? parseInt(rooms) : null,
                notes: notes || null,
                logo_file_id: logoFileId || null,
            });
            setVenue(data);
            toast.success("Venue registered. Awaiting admin approval.");
        } catch (e2) {
            toast.error(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="overline text-muted-foreground">Loading...</div>
            </div>
        );
    }

    if (venue) {
        return (
            <div className="min-h-screen bg-background" data-testid="venue-already-registered">
                <Header />
                <section className="max-w-3xl mx-auto px-6 py-20">
                    <div className="overline mb-4">Venue Channel</div>
                    <h1 className="font-serif text-4xl mb-6">{venue.name}</h1>
                    <VenueStatusBadge status={venue.status} />
                    <div className="mt-8 p-6 border border-white/10 bg-card rounded-sm space-y-2">
                        <div className="overline">Invite code</div>
                        <div className="font-mono text-2xl gold-text tracking-[0.3em]">{venue.invite_code}</div>
                        <div className="text-xs text-muted-foreground">Share with your team to onboard staff to your venue account.</div>
                    </div>
                    <div className="mt-8 flex gap-4">
                        <Button
                            onClick={() => navigate("/venue/dashboard")}
                            className="bg-primary text-primary-foreground rounded-sm uppercase tracking-[0.2em] text-xs h-11"
                            data-testid="venue-go-dashboard-btn"
                        >
                            Open dashboard <ArrowRight size={14} className="ml-2" />
                        </Button>
                    </div>
                </section>
                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background" data-testid="venue-register-page">
            <Header />
            <section className="max-w-7xl mx-auto px-6 lg:px-10 py-12 lg:py-16">
                <div className="grid lg:grid-cols-12 gap-16">
                    <div className="lg:col-span-5">
                        <div className="overline mb-4">Venue Partners</div>
                        <h1 className="font-serif text-5xl sm:text-6xl tracking-tighter leading-[0.95]">
                            Hospitality,<br /><span className="italic gold-grad">elevated.</span>
                        </h1>
                        <p className="mt-8 text-base text-muted-foreground leading-relaxed">
                            Hotels, villas and Airbnb hosts: book SMART services for your guests in seconds. Cleanings between turnovers, in-villa massage, private chef dinners, airport transfers — all on one monthly invoice.
                        </p>
                        <div className="mt-10 p-6 bg-card border border-primary/30 rounded-sm">
                            <div className="overline mb-3">How it works</div>
                            <ul className="space-y-2 text-xs text-muted-foreground">
                                <li>1. Register your venue and get approved.</li>
                                <li>2. Book any service for any guest, any room.</li>
                                <li>3. We dispatch a vetted SMART pro within hours.</li>
                                <li>4. Receive a single monthly invoice — your <span className="gold-text">5% affiliate share</span> is auto-deducted.</li>
                            </ul>
                            <div className="mt-4 text-xs text-muted-foreground">High-volume venues qualify for a <span className="gold-text">10% rebate</span> automatically.</div>
                        </div>
                    </div>
                    <div className="lg:col-span-7">
                        <form
                            onSubmit={submit}
                            className="bg-card border border-white/10 p-8 lg:p-10 rounded-sm space-y-5"
                            data-testid="venue-register-form"
                        >
                            <div>
                                <div className="overline mb-2">Application</div>
                                <h2 className="font-serif text-3xl">Tell us about your venue.</h2>
                            </div>

                            <Field label="Venue name *">
                                <Input value={name} onChange={(e) => setName(e.target.value)} required className="bg-input border-white/10 h-11 rounded-sm" data-testid="venue-name-input" />
                            </Field>

                            <div className="grid sm:grid-cols-2 gap-4">
                                <Field label="Type">
                                    <select
                                        value={venueType}
                                        onChange={(e) => setVenueType(e.target.value)}
                                        className="bg-input border border-white/10 h-11 rounded-sm w-full px-3 text-sm uppercase tracking-[0.15em]"
                                        data-testid="venue-type-select"
                                    >
                                        {VENUE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                </Field>
                                <Field label="Rooms / units">
                                    <Input type="number" value={rooms} onChange={(e) => setRooms(e.target.value)} className="bg-input border-white/10 h-11 rounded-sm" data-testid="venue-rooms-input" />
                                </Field>
                            </div>

                            <div className="grid sm:grid-cols-2 gap-4">
                                <Field label="Contact name *">
                                    <Input value={contactName} onChange={(e) => setContactName(e.target.value)} required className="bg-input border-white/10 h-11 rounded-sm" data-testid="venue-contact-name-input" />
                                </Field>
                                <Field label="Contact email *">
                                    <Input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} required className="bg-input border-white/10 h-11 rounded-sm" data-testid="venue-contact-email-input" />
                                </Field>
                            </div>

                            <Field label="Contact phone">
                                <Input value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} className="bg-input border-white/10 h-11 rounded-sm" data-testid="venue-contact-phone-input" />
                            </Field>

                            <div className="grid sm:grid-cols-3 gap-4">
                                <Field label="Address *">
                                    <Input value={address} onChange={(e) => setAddress(e.target.value)} required className="bg-input border-white/10 h-11 rounded-sm sm:col-span-2" data-testid="venue-address-input" />
                                </Field>
                                <Field label="City *">
                                    <select value={city} onChange={(e) => setCity(e.target.value)} className="bg-input border border-white/10 h-11 rounded-sm w-full px-3 text-sm" data-testid="venue-city-select">
                                        {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </Field>
                            </div>

                            <Field label="Tell us about your guests / season">
                                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="bg-input border-white/10 rounded-sm" data-testid="venue-notes-input" />
                            </Field>

                            <FileUpload
                                purpose="venue_logo"
                                value={logoFileId}
                                onUploaded={(f) => setLogoFileId(f.id)}
                                onCleared={() => setLogoFileId("")}
                                label="Venue logo (optional)"
                                accept="image/*"
                                testid="venue-logo"
                            />

                            <Button
                                type="submit"
                                disabled={submitting}
                                className="w-full bg-primary text-primary-foreground rounded-sm uppercase tracking-[0.2em] text-xs h-12"
                                data-testid="venue-submit-btn"
                            >
                                {submitting ? "Submitting..." : "Register Venue"} <ArrowRight size={14} className="ml-2" />
                            </Button>
                            <p className="text-[11px] text-muted-foreground">
                                By registering you agree to monthly net-30 billing terms and the SMART venue partner agreement.
                            </p>
                        </form>
                    </div>
                </div>
            </section>
            <Footer />
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <Label className="overline mb-2 block">{label}</Label>
            {children}
        </div>
    );
}

export function VenueStatusBadge({ status }) {
    const config = {
        pending: { label: "Pending Approval", icon: Hourglass, color: "bg-amber-900/30 text-amber-300 border-amber-700/40" },
        approved: { label: "Active", icon: BadgeCheck, color: "bg-emerald-900/30 text-emerald-300 border-emerald-700/40" },
        suspended: { label: "Suspended", icon: X, color: "bg-red-900/30 text-red-300 border-red-700/40" },
    }[status] || { label: status, icon: ShieldCheck, color: "bg-card border-white/10" };
    const Icon = config.icon;
    return (
        <div className={`inline-flex items-center gap-2 px-4 py-2 border rounded-sm text-xs uppercase tracking-[0.2em] ${config.color}`} data-testid={`venue-status-${status}`}>
            <Icon size={14} strokeWidth={1.5} /> {config.label}
        </div>
    );
}
